"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DisclosureController = void 0;
const utils_1 = require("@/utils");
/**
 * 열고 닫는 모든 disclosure의 트리거 클릭 토글, aria-expanded 동기화, toggle 이벤트 발행을 소유한다.
 * 여는 표면의 종류(aria-haspopup)는 호스트가 추정하지 않고 트리거가 표준 attribute로 직접 선언한다.
 * 열림 상태 자체는 공개 API라 호스트의 reflected property로 남기고, 컨트롤러는 읽기/쓰기만 위임받는다.
 * `OpenState` 호스트는 isOpen·setOpen 없이 넘기면 `open`을 읽고 `show()`·`close()`로 바꾼다.
 * 트리거는 getTrigger로 지정하며, 생략하면 aria-controls로 호스트를 가리키는 외부 요소를 기본값으로 찾는다.
 * 그 요소를 찾을 root는 getRoot로 바꿀 수 있다. portal로 옮겨지는 호스트는 옮기기 전 자리의 root를 넘겨,
 * 소비자의 shadow 안에 있는 트리거도 찾는다.
 * 외부 클릭·ESC로 스스로 닫히는 동작은 overlay 표면이 각자 소유한다.
 */
class DisclosureController {
    constructor(host, options = {}) {
        this.host = host;
        this.handleTriggerClick = () => {
            this.toggle();
        };
        this.options = {
            isOpen: () => !!host.open,
            setOpen: open => (open ? host.show?.() : host.close?.()),
            ...options,
        };
        host.addController(this);
    }
    toggle() {
        this.options.setOpen(!this.options.isOpen());
    }
    hostDisconnected() {
        this.unwireTrigger();
    }
    hostUpdated() {
        this.syncTrigger();
        this.dispatchToggle();
    }
    /** 이 표면의 트리거. 표면이 기준 위치로 쓰기도 한다(mm-popover). */
    get trigger() {
        const explicit = this.options.getTrigger?.();
        if (explicit)
            return explicit;
        if (!this.host.id)
            return undefined;
        const root = this.options.getRoot?.() ?? this.host.getRootNode();
        return root.querySelector(`[aria-controls="${this.host.id}"]`) ?? undefined;
    }
    // 트리거가 바뀌면 클릭 배선을 옮기고, 열림 상태를 aria로 반영한다.
    syncTrigger() {
        const trigger = this.trigger;
        if (trigger !== this.wiredTrigger) {
            this.unwireTrigger();
            trigger?.addEventListener('click', this.handleTriggerClick);
            this.wiredTrigger = trigger;
        }
        if (!trigger)
            return;
        trigger.setAttribute('aria-expanded', String(this.options.isOpen()));
    }
    // 호스트가 열리거나 닫히면 호스트 요소에서 toggle 이벤트를 디스패치한다. 호스트를 쓰는 쪽(소비자)은
    // 이 이벤트에 리스너를 달아, 호스트가 외부 클릭·ESC처럼 스스로 닫힌 경우에도 자기 상태(펼침 표시 등)를 맞춘다.
    // 열림 여부는 렌더가 끝날 때마다 직전 값과 비교해 판단하므로, 트리거·외부 클릭·ESC·소비자의 open 변경 중
    // 어느 경로로 바뀌었든 한 번씩 나간다. 첫 렌더는 바뀐 것이 아니므로 비교할 값만 기록한다.
    // 네이티브 toggle처럼 버블링하지 않아 호스트에 직접 단 리스너만 받는다. 안에 둔 다른 disclosure의 toggle이
    // 부모로 올라가 바깥 리스너에 섞이지 않게 하기 위해서다.
    dispatchToggle() {
        const open = this.options.isOpen();
        const wasOpen = this.wasOpen;
        this.wasOpen = open;
        if (wasOpen === undefined || wasOpen === open)
            return;
        (0, utils_1.emit)(this.host, 'toggle', { open }, { bubbles: false, composed: false });
    }
    unwireTrigger() {
        this.wiredTrigger?.removeEventListener('click', this.handleTriggerClick);
        this.wiredTrigger = undefined;
    }
}
exports.DisclosureController = DisclosureController;
//# sourceMappingURL=data:application/json;base64,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